import { router } from "@inertiajs/react"
import { Ban, X } from "lucide-react"
import { cn } from "@/lib/utils"
import { ChannelIcon } from "@/lib/channel-icons"
import type { CalendarBlock, CalendarChannel } from "./types"

// Shared helpers for rendering operator-placed calendar blocks.

// True when the block should show given the current channel filter — at
// least one of the block's channels is visible.
export function blockVisible(block: CalendarBlock, visibleChannels: Set<string>) {
  return block.channels.some((slug) => visibleChannels.has(slug))
}

// True when [starts_at, ends_at) overlaps the given local-time hour cell.
export function blockOverlapsHour(block: CalendarBlock, date: Date, hour: number) {
  const cellStart = new Date(date.getFullYear(), date.getMonth(), date.getDate(), hour, 0, 0, 0)
  const cellEnd = new Date(cellStart.getTime() + 60 * 60 * 1000)
  const from = new Date(block.starts_at)
  const to = new Date(block.ends_at)
  return from < cellEnd && to > cellStart
}

// True when [starts_at, ends_at) overlaps the given local calendar day.
export function blockOverlapsDay(block: CalendarBlock, date: Date) {
  const dayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate())
  const dayEnd = new Date(dayStart.getTime() + 24 * 60 * 60 * 1000)
  const from = new Date(block.starts_at)
  const to = new Date(block.ends_at)
  return from < dayEnd && to > dayStart
}

// True when some block covers the exact instant for the channel — used to
// hide "open slot" template ghosts that a block has made unavailable.
export function instantBlocked(blocks: CalendarBlock[], channel: string, iso: string) {
  const t = new Date(iso).getTime()
  return blocks.some(
    (b) =>
      b.channels.includes(channel) &&
      t >= new Date(b.starts_at).getTime() &&
      t < new Date(b.ends_at).getTime()
  )
}

export function blockRangeLabel(block: CalendarBlock) {
  return `${formatTime(new Date(block.starts_at))} – ${formatTime(new Date(block.ends_at))}`
}

function formatTime(d: Date) {
  const h = d.getHours()
  const m = d.getMinutes()
  const h12 = h % 12 === 0 ? 12 : h % 12
  const period = h < 12 ? "AM" : "PM"
  return m === 0 ? `${h12} ${period}` : `${h12}:${String(m).padStart(2, "0")} ${period}`
}

export function deleteBlock(block: CalendarBlock) {
  if (!window.confirm(`Remove this block (${blockRangeLabel(block)})? The time becomes available for scheduling again.`)) return
  router.delete(`/calendar/blocks/${block.id}`, { preserveScroll: true })
}

// The chip rendered on the calendar grids. `compact` drops the channel
// icons and shortens the label for tight month cells.
export function CalendarBlockChip({
  block,
  channels,
  compact = false,
  className,
}: {
  block: CalendarBlock
  channels: CalendarChannel[]
  compact?: boolean
  className?: string
}) {
  const blockChannels = channels.filter((c) => block.channels.includes(c.slug))
  const names = blockChannels.map((c) => c.name).join(", ")

  return (
    <div
      className={cn(
        "group flex items-center gap-1 rounded border border-hairline bg-hairline/40 px-1.5 py-0.5 text-[10px] text-ink-muted",
        className
      )}
      title={`Blocked ${blockRangeLabel(block)} · ${names}`}
    >
      <Ban className="h-3 w-3 shrink-0" aria-hidden />
      <span className="truncate font-medium">Blocked · {blockRangeLabel(block)}</span>
      {!compact && (
        <span className="flex shrink-0 items-center gap-0.5">
          {blockChannels.map((c) => (
            <ChannelIcon key={c.slug} channelType={c.channel_type} color={c.color} className="h-3 w-3" />
          ))}
        </span>
      )}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          deleteBlock(block)
        }}
        aria-label="Remove block"
        className="ml-auto shrink-0 rounded p-0.5 opacity-0 transition-opacity hover:bg-hairline group-hover:opacity-100 focus-visible:opacity-100"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  )
}
